import { deserializeLoadFailure } from './loadFailure';
import type {
    FetchRandomSymbolsResponse,
    FetchSecurityMasterEntriesMatchingPrefixResponse,
    FetchSecurityMasterEntriesResponse,
    FetchSymbolsMatchingPrefixResponse,
    SecurityMasterQueryMasterResponse,
    SecurityMasterServiceRequest,
    SecurityMasterServiceResponse,
} from './types';
import type { LocalDate } from '@js-joda/core';
import { computed } from '@preact/signals-react';
import { deferred } from '@thinkalpha/ui-core/deferred';
import { injectable } from 'inversify';
import { inject } from 'src/features/ioc';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getSecurityMasterLoadQuery, invalidateSecurityMasterQueries } from 'src/queries/securityMaster';
import type { SecurityMasterService } from 'src/services/SecurityMasterService';
import type { SessionService } from 'src/services/SessionService';
import type { ReactBindings } from 'src/types/bindings';
import { v4 as uuid } from 'uuid';

@injectable()
export class SecurityMasterServiceImpl implements SecurityMasterService {
    #worker: Worker;
    #logger: ReactBindings['Logger'];
    readonly #ready: Promise<void>;
    readonly #loadQuery: QueryObserverModel<string, Error, string>;

    constructor(
        @inject('Logger') logger: ReactBindings['Logger'],
        @inject('Store') store: ReactBindings['Store'],
        @inject('QueryClient') queryClient: ReactBindings['QueryClient'],
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('SessionService') sessionService: SessionService,
    ) {
        this.#worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
        this.#logger = logger.getSubLogger({ name: 'SecurityMasterServiceImpl' });

        // The worker's store is the source of truth for master state, so it
        // emits `master-changed` whenever the content actually changes (cache
        // hydration, first download, or a refresh with different data). Each
        // change invalidates every mounted lookup under the namespace; the
        // first one also means the master is ready.
        const ready = deferred<void>();
        this.#ready = ready.promise;
        this.#worker.addEventListener('message', (e: MessageEvent<SecurityMasterServiceResponse>) => {
            if (e.data.type !== 'master-changed') return;
            ready.resolve();
            void invalidateSecurityMasterQueries(queryClient);
        });

        // The service owns keeping the master loaded and refreshed. Symbol
        // lookups (charts, command palette, TradingView) wait inside the
        // worker for the master to be populated, so the load must not depend
        // on some widget happening to mount a React query observer.
        this.#loadQuery = queryObserverModelFactory<string, Error, string>(
            computed(() => {
                const auth = store.signal.value.auth;
                const accessToken = auth?.masqAccessToken ?? sessionService.accessToken.value;
                return getSecurityMasterLoadQuery(accessToken, this);
            }),
        );
    }

    /**
     * Resolves once the worker's symbol store holds a usable master (cached
     * or freshly downloaded). Await this before treating a failed lookup as
     * "symbol does not exist".
     */
    get ready(): Promise<void> {
        return this.#ready;
    }

    async #sendWorkerRequest<TResult>(request: SecurityMasterServiceRequest): Promise<TResult> {
        this.#worker.postMessage(request);

        return new Promise((resolve) => {
            const listener = (e: MessageEvent<SecurityMasterServiceResponse>) => {
                if (e.data.id === request.id) {
                    this.#worker.removeEventListener('message', listener);
                    resolve(e.data as TResult);
                }
            };

            this.#worker.addEventListener('message', listener);
        });
    }

    async fetchSecurityMasterEntry(symbol: string) {
        // ToDo: Implement https://github.com/yornaath/batshit so we can useQueries on fetchSecurityMasterEntry
        const results = await this.#sendWorkerRequest<FetchSecurityMasterEntriesResponse>({
            type: 'fetch-security-master-entries',
            symbols: [symbol],
            id: uuid(),
        });

        const result = results.entries[symbol];

        if (TRACE) {
            this.#logger.trace('fetchSecurityMasterEntry', { results, symbol, result });
        }

        if (!result) {
            throw new Error('Symbol not found in security master');
        }

        return result;
    }

    async fetchSecurityMasterEntries(symbols: string[]) {
        // ToDo: Implement https://github.com/yornaath/batshit so we can useQueries on fetchSecurityMasterEntry
        return await this.#sendWorkerRequest<FetchSecurityMasterEntriesResponse>({
            type: 'fetch-security-master-entries',
            symbols: symbols,
            id: uuid(),
        }).then((res) => res.entries);
    }

    async fetchSecurityMaster(accessToken: string, _forDate: LocalDate, _features: string[]) {
        const response = await this.#sendWorkerRequest<SecurityMasterQueryMasterResponse>({
            type: 'query-master',
            accessToken,
            id: uuid(),
        });

        if (response.error) {
            throw deserializeLoadFailure(response.error);
        }

        return 'SECURITY_MASTER';
    }

    async fetchSymbolsMatchingPrefix(prefix: string) {
        const response = await this.#sendWorkerRequest<FetchSymbolsMatchingPrefixResponse>({
            type: 'fetch-symbol-list',
            id: uuid(),
            prefix,
        });

        return response.matchingSymbols;
    }

    async fetchSecurityMasterEntriesMatchingPrefix(prefix: string) {
        const response = await this.#sendWorkerRequest<FetchSecurityMasterEntriesMatchingPrefixResponse>({
            type: 'fetch-security-master-entries-matching-prefix',
            id: uuid(),
            prefix,
        });

        return response.entries;
    }

    async fetchRandomSymbols(count: number) {
        const response = await this.#sendWorkerRequest<FetchRandomSymbolsResponse>({
            type: 'fetch-random-symbols',
            id: uuid(),
            count,
        });

        return response.symbols;
    }

    [Symbol.dispose](): void {
        this.#loadQuery[Symbol.dispose]();
        this.#worker.terminate();
    }
}
